import PropTypes from 'prop-types';
import React, { createContext, useContext } from 'react';
import ReactDOM from 'react-dom';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import styled from 'styled-components';
import { getThemeProps } from 'ravenjs/utils/theme';

const DraggableStyled = styled.div`
    ${getThemeProps('DragAndDrop.styles.draggable')};
    ${({ draggableStyle }) => draggableStyle}
    ${({ isDragging }) => (isDragging ? getThemeProps('DragAndDrop.styles.dragging') : '')}
    ${({ draggingStyle, isDragging }) => (isDragging ? draggingStyle : '')}
`;

const DroppableStyled = styled.div`
    ${getThemeProps('DragAndDrop.styles.container')};
    ${({ droppableStyle }) => droppableStyle}
`;

const DragHandleContext = createContext(null);

export const DragHandle = ({ children }) => {
    const dragHandleProps = useContext(DragHandleContext);
    return <div {...dragHandleProps}>{children}</div>;
};

DragHandle.propTypes = {
    children: PropTypes.node,
};

DragHandle.defaultProps = {
    children: null,
};

const DragAndDrop = props => {
    const {
        items,
        onChange,
        styles: { draggable: draggableStyle, dragging: draggingStyle, droppable: droppableStyle },
        useSeparateDragHandle,
        usePortal,
    } = props;

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

    const onDragEnd = result => {
        if (!result.destination) {
            return;
        }

        onChange(reorder(items, result.source.index, result.destination.index));
    };

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable">
                {provided => (
                    <DroppableStyled
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        droppableStyle={droppableStyle}
                    >
                        {items.map((item, index) => (
                            <Draggable key={item.id} draggableId={String(item.id)} index={index}>
                                {(provided, snapshot) => {
                                    const draggableContent = (
                                        <DraggableStyled
                                            ref={provided.innerRef}
                                            draggableStyle={draggableStyle}
                                            draggingStyle={draggingStyle}
                                            isDragging={snapshot.isDragging}
                                            {...provided.draggableProps}
                                            {...(!useSeparateDragHandle
                                                ? provided.dragHandleProps
                                                : {})}
                                        >
                                            {useSeparateDragHandle ? (
                                                <DragHandleContext.Provider
                                                    value={provided.dragHandleProps}
                                                >
                                                    {item.content}
                                                </DragHandleContext.Provider>
                                            ) : (
                                                item.content
                                            )}
                                        </DraggableStyled>
                                    );

                                    if (usePortal && snapshot.isDragging) {
                                        return ReactDOM.createPortal(
                                            draggableContent,
                                            document.body
                                        );
                                    }

                                    return draggableContent;
                                }}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </DroppableStyled>
                )}
            </Droppable>
        </DragDropContext>
    );
};

DragAndDrop.propTypes = {
    items: PropTypes.array,
    onChange: PropTypes.func.isRequired,
    styles: PropTypes.shape({
        draggable: PropTypes.object,
        dragging: PropTypes.object,
        droppable: PropTypes.object,
    }),
    useSeparateDragHandle: PropTypes.bool,
    usePortal: PropTypes.bool,
};

DragAndDrop.defaultProps = {
    items: [],
    styles: {
        draggable: {},
        dragging: {},
        droppable: {},
    },
    useSeparateDragHandle: false,
    usePortal: false,
};

export default DragAndDrop;
